import { FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css'
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { fetchStart, fetchStop } from '../../../../redux/actions/common'
import { trim } from '../../../../_metronic/helpers'
import Alert from '../../../../_metronic/partials/alert'
import { parsePhoneNumberWithError } from 'libphonenumber-js/max'
import { isValidPhoneNumber } from 'libphonenumber-js'

const errorMsg = {
  required: 'Mobile number is Required',
  amountRange: 'Amount range should be between 100,000 - 3,000,000',
  durationRange: 'Duration range should be between 3 - 36 months',
}

// 1. Added 'email' to the props
const Step1Sub1: FC<any> = ({ sendOTP, setMobile, setOtpMethod: setParentOtpMethod, id, name, mobile, email }) => {
  const dispatch = useDispatch()
  const loanApplication = useSelector((state: any) => state.loan.loanApplication)
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [alertMsg, setAlertMsg] = useState<string>('')
  const [alertType, setAlertType] = useState<any>('primary')
  
  // State for OTP Method
  const [otpMethod, setOtpMethod] = useState<'sms' | 'email'>('sms')

  const initialValues = {
    applicantName: name ? name : '',
    mobileNumber: mobile ? mobile : '',
    emailAddress: email ? email : '',
  }

  const validationSchema = Yup.object().shape({
    applicantName: Yup.string()
      .required('Applicant Name is required')
      .test('unique', 'Applicant Name must not contain numbers', (value: any) => {
        return !/\d/.test(value)
      }),
    mobileNumber: Yup.string().when([], {
      is: () => otpMethod === 'sms',
      then: (schema) => schema
        .required(errorMsg.required)
        .test('mobileValidation', 'Please enter valid Singapore number', (value: any) => {
          if (!value) return false
          const number = parsePhoneNumberWithError(value, 'SG')
          return isValidPhoneNumber(value, 'SG') && number.isValid() && number.country === 'SG'
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
    emailAddress: Yup.string().when([], {
      is: () => otpMethod === 'email',
      then: (schema) => schema
        .required('Email Address is required')
        .email('Enter Valid Email'),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        const { mobileNumber } = trim(values)
        dispatch(fetchStart())
        
        // Always set mobile number
        setMobile(mobileNumber)
        
        // Update parent OTP method
        if (setParentOtpMethod) {
          setParentOtpMethod(otpMethod)
        }

        if (loanApplication?.applicationCompleteStep !== 3) {
          await sendOTP({
            variables: {
              mobileNo: mobileNumber,
              isApplicantOrShareholder: true,
              isCoApplicantKYB: true,
              id: id,
              type: otpMethod === 'sms' ? 'mobile' : 'email'
            },
          })
        } else {
          dispatch(fetchStop())
          setSubmitting(false)
          setShowAlert(true)
          setAlertType('primary')
          setAlertMsg('Application KYB Already Completed')
        }
      } catch (error) {
        console.error('Error sending OTP:', error)
      } finally {
        dispatch(fetchStop())
        setSubmitting(false)
      }
    },
  })

  return (
    <div className='d-flex flex-column '>
      <div className='notice d-flex banner text-start p-6 mb-5'>
        <div className='d-flex flex-stack flex-grow-1'>
          <div>
            <h2>Company / Business eKYC </h2>
            <div className='fs-3'>
              You have been marked as an authorized person for your company. Please complete your
              business eKYC via Myinfo Business.
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='cardNew'>
          <div className='section-heading'>
            <h2>Applicant Details</h2>
            <hr className='text-gray-400' />
          </div>

          <div className='row'>
            <div className='col-md-12 mb-4'>
              <label htmlFor='' className='input-field-label required'>
                Full Name
              </label>
              <div className='col-sm-12'>
                <input
                  name='applicantName'
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='Enter your full name'
                  value={formik.values.applicantName}
                  onChange={formik.handleChange}
                  disabled={true}
                  style={{ cursor: 'not-allowed', backgroundColor: '#eff2f5' }}
                />
                {formik.touched.applicantName && formik.errors.applicantName && (
                  <div className='text-danger mt-2'>{formik.errors.applicantName}</div>
                )}
              </div>
            </div>

            {/* Verify Your Account Section */}
            <div className='col-md-12 mb-2'>
              <label className='input-field-label fw-bold' style={{ fontSize: '16px' }}>
                Secure Identity Verification
              </label>
              <p className='text-muted mb-0' style={{ fontSize: '13px', marginTop: '4px' }}>
                We'll send you a one-time verification code to confirm your identity
              </p>
            </div>

            {/* Choose verification method */}
            <div className='col-md-12 mb-4'>
              <label className='input-field-label mb-2' style={{ fontSize: '14px' }}>
                How would you like to receive your code?
              </label>
              <div>
                <div 
                  className='d-inline-flex align-items-center gap-1 p-1' 
                  style={{
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
                    borderRadius: '50px',
                    border: '1px solid #e0e5eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                >
                  <div
                    onClick={() => setOtpMethod('sms')}
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      padding: '4px 12px',
                      borderRadius: '50px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontWeight: '700',
                      fontSize: '12px',
                      background: otpMethod === 'sms' ? 'var(--primary-btn-background)' : 'transparent',
                      color: otpMethod === 'sms' ? '#ffffff' : '#495057',
                      boxShadow: otpMethod === 'sms' ? '0 3px 10px rgba(0,0,0,0.2)' : 'none',
                      transform: otpMethod === 'sms' ? 'scale(1.02)' : 'scale(1)',
                    }}
                  >
                    SMS
                  </div>
                  <div
                    onClick={() => setOtpMethod('email')}
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      padding: '4px 12px',
                      borderRadius: '50px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontWeight: '700',
                      fontSize: '12px',
                      background: otpMethod === 'email' ? 'var(--primary-btn-background)' : 'transparent',
                      color: otpMethod === 'email' ? '#ffffff' : '#495057',
                      boxShadow: otpMethod === 'email' ? '0 3px 10px rgba(0,0,0,0.2)' : 'none',
                      transform: otpMethod === 'email' ? 'scale(1.02)' : 'scale(1)',
                    }}
                  >
                    E-mail
                  </div>
                </div>
              </div>
            </div>

            {/* Conditional Phone Number or Email Field */}
            {otpMethod === 'sms' ? (
              <div className='col-md-12 mb-5'>
                <label htmlFor='' className='input-field-label required'>
                  Phone Number
                </label>
                <div className='col-sm-12'>
                  <PhoneInput
                    id='mobileNumber'
                    international
                    defaultCountry='SG'
                    value={formik.values.mobileNumber}
                    onChange={(value) => formik.setFieldValue('mobileNumber', value)}
                    placeholder='Enter phone number'
                    className='int-phone-input'
                  />
                  {(formik.touched.mobileNumber || formik.values.mobileNumber === undefined) &&
                    formik.errors.mobileNumber && (
                      <div className='text-danger mt-2'>{formik.errors.mobileNumber}</div>
                    )}
                </div>
              </div>
            ) : (
              <div className='col-md-12 mb-5'>
                <label htmlFor='' className='input-field-label required'>
                  Address
                </label>
                <div className='col-sm-12'>
                  <input
                    name='emailAddress'
                    type='email'
                    className='form-control form-control-solid'
                    placeholder='your email here'
                    value={formik.values.emailAddress}
                    onChange={formik.handleChange}
                    disabled={true}
                    style={{ cursor: 'not-allowed', backgroundColor: '#eff2f5' }}
                  />
                  {formik.touched.emailAddress && formik.errors.emailAddress && (
                    <div className='text-danger mt-2'>{formik.errors.emailAddress}</div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        <div className='cardNew stickeyFooter'>
          <div className='d-flex flex-end'>
            <button type='submit' className='button button-primary' disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Sending...' : 'Send OTP'}
            </button>
          </div>
        </div>
      </form>

      {showAlert && (
        <Alert variant={alertType} showAlert={showAlert} setShowAlert={setShowAlert}>
          {alertMsg}
        </Alert>
      )}
    </div>
  )
}

export { Step1Sub1 }